import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { auctionTemplatePropertiesType } from '../../types';
import ExpandedListTab from '../ExpandedListTab';
import SaveButton from '../buttons/SaveButton';
import TextField from '../fields/TextField';
import {
    AUCTION_TEMPLATE_DESCRIPTION_MAX_LENGTH,
    AUCTION_TEMPLATE_NAME_LENGTH,
    auctionTemplateAbsoluteDeviationValidator,
    auctionTemplateDeliveryYearValidator,
    auctionTemplateDescriptionValidator,
    auctionTemplateMaxBidSizeValidator,
    auctionTemplateMaxPriceValidator,
    auctionTemplateMinPriceValidator,
    auctionTemplateNameValidator,
    auctionTemplatePowerValidator,
    auctionTemplateQuantityValueValidator,
    auctionTemplateRelativeDeviationValidator, auctionTemplateRevenueSplitValidator,
    auctionTemplateSmallestQuantityUnitValidator,
    auctionTemplateSpotReferencePriceValidator,
    auctionTemplateTimeValidator,
} from '../../validators/auctionTemplates';
import SelectField from '../fields/SelectField';
import useExtendedState from '../../hooks/useExtendedState';
import { ENERGY, getProductType, SECURITY } from '../../utils/ProductTypes';
import useMultiValidator from '../../hooks/useMultiValidator';
import Constants from '../../utils/Constants';
import { date, getCurrentYear } from '../../utils';
import { getAuctionType } from '../../utils/AuctionTypes';
import { getBiddingType, OPEN, SLOTTED, UNKNOWN as UNKNOWN_BIDDING_TYPE } from '../../utils/BiddingTypes';
import { BID_PRICE, getTradePriceCalculationType, UNKNOWN as UNKNOWN_TRADE_PRICE_CALCULATION_TYPE } from '../../utils/TradePriceCalculationTypes';
import { getAuctionAlgorithm } from '../../utils/AuctionAlgorithms';
import { getAuctionAcceptanceType } from '../../utils/AuctionAcceptanceTypes';
import CheckboxField from '../fields/CheckboxField';
import NumberField from '../fields/NumberField';
import { getSettlementStartType } from '../../utils/SettlementStartTypes';
import Unit from '../Unit';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_AUCTION_TEMPLATES_QUERY_NAME, CREATE_AUCTION_TEMPLATE } from '../../graphql/auctionTemplates';
import TimeField, { HOUR_AND_MINUTE } from '../fields/TimeField';
import useSingleValidator from '../../hooks/useSingleValidator';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

AuctionTemplateCreateTab.propTypes = {
    hideHandler: PropTypes.func.isRequired,
    auctionTemplateProps: auctionTemplatePropertiesType.isRequired,
};

function AuctionTemplateCreateTab(props) { // NOSONAR javascript:S3776
    const {
        hideHandler,
        auctionTemplateProps,
    } = props;
    const {
        availableProducts,
        availableZones,
        availableAuctioneers,
        availableAuctionTypes,
        availableBiddingTypes,
        availableTradePriceCalculationTypes,
        availableAuctionAlgorithms,
        availableAuctionAcceptanceTypes,
        availableSettlStartTypes,
        defaultAuctionDurationHours,
    } = auctionTemplateProps;
    const { t } = useTranslation();
    const availableTechnicalAuctioneers = availableAuctioneers?.filter((availableAuctioneer) => availableAuctioneer.technicalAuctioneer);
    const nameState = useExtendedState('');
    const deliveryYearState = useExtendedState(null);
    const productState = useExtendedState('');
    const [productType, setProductType] = useState(getProductType(productState.value?.productType));
    const zoneState = useExtendedState('');
    const [currencyPostfix, setCurrencyPostfix] = useState(zoneState.value && <>&nbsp;{zoneState.value.currency.code}</>);
    const auctioneerState = useExtendedState('');
    const auctionTypeState = useExtendedState(
        availableAuctionTypes.length === 1
            ? availableAuctionTypes[0]
            : '',
    );
    const quantityValueState = useExtendedState(null);
    const powerState = useExtendedState(null);
    const openingTimeState = useExtendedState('');
    const closingTimeState = useExtendedState('');
    const showLastPriceState = useExtendedState(false);
    const showRankingState = useExtendedState(false);
    const limitBidModificationState = useExtendedState(false);
    const minPriceState = useExtendedState(null);
    const maxPriceState = useExtendedState(null);
    const spotReferencePriceState = useExtendedState(null);
    const absoluteDeviationState = useExtendedState(null);
    const relativeDeviationState = useExtendedState(null);
    const biddingTypeState = useExtendedState(
        availableBiddingTypes.length === 1
            ? getBiddingType(availableBiddingTypes[0])
            : UNKNOWN_BIDDING_TYPE,
    );
    const tradePriceCalculationTypeState = useExtendedState(
        availableTradePriceCalculationTypes.length === 1
            ? getTradePriceCalculationType(availableTradePriceCalculationTypes[0])
            : UNKNOWN_TRADE_PRICE_CALCULATION_TYPE,
    );
    const acceptanceTypeState = useExtendedState(
        availableAuctionAcceptanceTypes.length === 1
            ? availableAuctionAcceptanceTypes[0]
            : '',
    );
    const algorithmState = useExtendedState(
        availableAuctionAlgorithms.length === 1
            ? availableAuctionAlgorithms[0]
            : '',
    );
    const smallestQuantityUnitState = useExtendedState(null);
    const maxBidSizeState = useExtendedState(null);
    const settlementStartTypeState = useExtendedState('');
    const descriptionState = useExtendedState('');
    const revenueSplitState = useExtendedState(0);
    const { validators, isValid } = useMultiValidator(true, {
        name: compositeValidator(auctionTemplateNameValidator(t), wafValidator(t)),
        deliveryYear: auctionTemplateDeliveryYearValidator(t),
        quantityValue: compositeValidator(auctionTemplateQuantityValueValidator(t, productType), wafValidator(t)),
        power: compositeValidator(auctionTemplatePowerValidator(t), wafValidator(t)),
        openingTime: auctionTemplateTimeValidator(t),
        closingTime: auctionTemplateTimeValidator(t),
        description: compositeValidator(auctionTemplateDescriptionValidator(t), wafValidator(t)),
        spotReferencePrice: auctionTemplateSpotReferencePriceValidator(t, Constants.MIN_SPOT_REFERENCE_PRICE, Constants.MAX_SPOT_REFERENCE_PRICE),
        absoluteDeviation: auctionTemplateAbsoluteDeviationValidator(t, Constants.MIN_ABSOLUTE_DEVIATION, Constants.MAX_ABSOLUTE_DEVIATION),
        relativeDeviation: auctionTemplateRelativeDeviationValidator(t, Constants.MIN_RELATIVE_DEVIATION, Constants.MAX_RELATIVE_DEVIATION),
        revenueSplit: auctionTemplateRevenueSplitValidator(t, Constants.MIN_REVENUE_SPLIT, Constants.MAX_REVENUE_SPLIT)
    }, { name: false });
    const {
        validator: minPriceValidator,
        isValid: isMinPriceValid
    } = useSingleValidator(true, useMemo(() => compositeValidator(auctionTemplateMinPriceValidator(t, maxPriceState.value), wafValidator(t)), [t, maxPriceState.value]));
    const {
        validator: maxPriceValidator,
        isValid: isMaxPriceValid
    } = useSingleValidator(true, useMemo(() => compositeValidator(auctionTemplateMaxPriceValidator(t, minPriceState.value), wafValidator(t)), [t, minPriceState.value]));
    const {
        validator: smallestQuantityUnitValidator,
        isValid: isSmallestQuantityUnitValid,
        reset: resetSmallestQuantityUnitValidator,
    } = useSingleValidator(true, useMemo(() => compositeValidator(auctionTemplateSmallestQuantityUnitValidator(
        t,
        Constants.MIN_SMALLEST_UNIT,
        quantityValueState.value,
        maxBidSizeState.value
    ), wafValidator(t)), [t, quantityValueState.value, maxBidSizeState.value]));
    const {
        validator: maxBidSizeValidator,
        isValid: isMaxBidSizeValid,
        reset: resetMaxBidSizeValidator,
    } = useSingleValidator(true, useMemo(() => compositeValidator(auctionTemplateMaxBidSizeValidator(
        t,
        Constants.MIN_MAXIMUM_BID_SIZE,
        smallestQuantityUnitState.value,
        quantityValueState.value
    ), wafValidator(t)), [t, smallestQuantityUnitState.value, quantityValueState.value]));
    const handleNewOpeningTime = (newOpeningTime) => {
        const oldOpeningTime = openingTimeState.value;
        openingTimeState.setValue(newOpeningTime);
        if (newOpeningTime instanceof Date) {
            if (oldOpeningTime instanceof Date) {
                if (closingTimeState.value instanceof Date) {
                    const newClosingTime = date(newOpeningTime, (d) => {
                        const hoursDiff = Math.abs(closingTimeState.value - oldOpeningTime) / 36e5;
                        d.setHours(d.getHours() + hoursDiff);
                    });
                    closingTimeState.setValue(newClosingTime);
                }
            } else if (!(closingTimeState.value instanceof Date)) {
                const newClosingTime = date(newOpeningTime, (d) => d.setHours(d.getHours() + defaultAuctionDurationHours));
                closingTimeState.setValue(newClosingTime);
            }
        }
    };
    const keyToProduct = useCallback((key) => key ? availableProducts.find((p) => p.productId === key) : '', [availableProducts]);
    const onProductSelect = (newProduct) => {
        deliveryYearState.reset();
        quantityValueState.reset();
        powerState.reset();
        settlementStartTypeState.reset();
        setProductType(getProductType(newProduct?.productType));
    };
    const keyToZone = useCallback((key) => key ? availableZones.find((z) => z.code === key) : '', [availableZones]);
    const onZoneSelect = (newZone) => {
        setCurrencyPostfix(newZone && <>&nbsp;{newZone.currency.code}</>);
    };
    const keyToAuctioneer = useCallback((key) => key ? availableTechnicalAuctioneers.find((a) => a.memberId === key) : '', [availableTechnicalAuctioneers]);
    const keyToBiddingType = (key) => getBiddingType(key);
    const onBiddingTypeSelect = () => {
        showRankingState.reset();
        limitBidModificationState.reset();
        smallestQuantityUnitState.reset();
        resetSmallestQuantityUnitValidator();
        maxBidSizeState.reset();
        resetMaxBidSizeValidator();
    };
    const keyToTradePriceCalculationTypeSelect = (key) => getTradePriceCalculationType(key);
    const onTradePriceCalculationTypeSelect = () => {
        showLastPriceState.reset();
    };

    const [isDisabled, setIsDisabled] = useState(true);
    Promise.all([isValid(), isMinPriceValid(), isMaxPriceValid(), isSmallestQuantityUnitValid(), isMaxBidSizeValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const variables = {
        name: nameState.value,
        deliveryYear: productType.is(ENERGY) && deliveryYearState.value ? parseInt(deliveryYearState.value, 10) : null,
        productId: productState.value ? productState.value.productId : null,
        quantity: quantityValueState.value,
        power: productType.is(ENERGY) && powerState.value ? parseInt(powerState.value, 10) : null,
        openingTime: openingTimeState.value instanceof Date ? openingTimeState.value.toTimeString().substring(0, 5) : null,
        closureTime: closingTimeState.value instanceof Date ? closingTimeState.value.toTimeString().substring(0, 5) : null,
        showLastPrice: tradePriceCalculationTypeState.value.is(BID_PRICE) ? showLastPriceState.value : null,
        showRanking: biddingTypeState.value.is(SLOTTED) ? showRankingState.value : null,
        limitBidModification: biddingTypeState.value.is(SLOTTED) ? limitBidModificationState.value : null,
        minPrice: minPriceState.value,
        maxPrice: maxPriceState.value,
        description: descriptionState.value,
        spotReferencePrice: spotReferencePriceState.value,
        relativeDeviation: relativeDeviationState.value,
        absoluteDeviation: absoluteDeviationState.value,
        zoneCode: zoneState.value ? zoneState.value.code : null,
        auctioneerMemberId: auctioneerState.value ? auctioneerState.value.memberId : null,
        auctionType: auctionTypeState.value || null,
        biddingType: biddingTypeState.value.is(UNKNOWN_BIDDING_TYPE) ? null : biddingTypeState.value.serverType,
        tradePriceCalculationType: tradePriceCalculationTypeState.value.is(UNKNOWN_TRADE_PRICE_CALCULATION_TYPE) ? null : tradePriceCalculationTypeState.value.serverType,
        auctionAlgorithm: algorithmState.value || null,
        auctionAcceptanceType: acceptanceTypeState.value || null,
        smallestUnit: biddingTypeState.value.is(OPEN) && smallestQuantityUnitState.value ? smallestQuantityUnitState.value : null,
        maxBidSize: biddingTypeState.value.is(OPEN) && maxBidSizeState.value ? maxBidSizeState.value : null,
        settlStartType: productType.is(SECURITY) && settlementStartTypeState.value ? settlementStartTypeState.value : null,
        revenueSplit: revenueSplitState.value,
    };
    const extendedMutation = useExtendedMutation(CREATE_AUCTION_TEMPLATE, {
        variables,
        successCallback: hideHandler,
        successMessageOptions: { key: 'auction-templates.notification.auction-template-create-success', dataProperty: 'createAuctionTemplate' },
        failureMessageOptions: { key: 'auction-templates.notification.auction-template-create-failure', params: { name: nameState.value } },
        refetchQueries: [ALL_AUCTION_TEMPLATES_QUERY_NAME],
    });
    return (
        <ExpandedListTab
            className="auction-template-create-tab"
            title={t('auction-templates.auction-template-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <TextField
                    label={t('auction-templates.name')}
                    state={nameState}
                    className="auction-template-create-tab--fields--name wide"
                    inputId="auction-template-create-tab--fields--name"
                    validator={validators.name}
                    maxLength={AUCTION_TEMPLATE_NAME_LENGTH}
                    placeholder={t('auctions.placeholder.name')}
                    isRequired
                />
                {availableProducts?.length > 0 && (
                    <SelectField
                        label={t('auction-templates.product')}
                        onChange={onProductSelect}
                        className="auction-template-create-tab--fields--product"
                        inputId="auction-template-create-tab--fields--product"
                        state={productState}
                        value={productState.value ? productState.value.productId : productState.value}
                        valueTransformer={keyToProduct}
                        emptyOption={<option value="">&nbsp;</option>}
                        options={availableProducts.map((p) => <option value={p.productId} key={p.productId}>{p.name} ({p.productId})</option>)}
                    />
                )}
                {availableZones?.length > 1 && (
                    <SelectField
                        label={t('auction-templates.zone')}
                        onChange={onZoneSelect}
                        className="auction-template-create-tab--fields--zone"
                        inputId="auction-template-create-tab--fields--zone"
                        state={zoneState}
                        valueTransformer={keyToZone}
                        value={zoneState.value ? zoneState.value.code : zoneState.value}
                        emptyOption={<option value="">&nbsp;</option>}
                        options={availableZones.map((z) => <option value={z.code} key={z.code}>{z.name} ({z.code})</option>)}
                    />
                )}
                {availableTechnicalAuctioneers?.length > 1 && (
                    <SelectField
                        label={t('auction-templates.auctioneer')}
                        className="auction-template-create-tab--fields--auctioneer"
                        inputId="auction-template-create-tab--fields--auctioneer"
                        state={auctioneerState}
                        valueTransformer={keyToAuctioneer}
                        value={auctioneerState.value ? auctioneerState.value.memberId : auctioneerState.value}
                        emptyOption={<option value="">&nbsp;</option>}
                        options={availableTechnicalAuctioneers.map((a) => <option value={a.memberId} key={a.memberId}>{a.name} ({a.memberId})</option>)}
                    />
                )}
                {productType.is(SECURITY) && (
                    <NumberField
                        label={t('auction-templates.revenue-split')}
                        className="auction-template-create-tab--fields--revenue-split"
                        inputId="auction-template-create-tab--fields--revenue-split"
                        state={revenueSplitState}
                        min={Constants.MIN_REVENUE_SPLIT}
                        max={Constants.MAX_REVENUE_SPLIT}
                        fractionDigits={0}
                        validator={validators.revenueSplit}
                        postfix={<>&nbsp;%</>}
                        step={1}
                    />
                )}
                <TimeField
                    label={t('auction-templates.opening-time')}
                    state={openingTimeState}
                    onChange={handleNewOpeningTime}
                    className="auction-template-create-tab--fields--opening-time wide"
                    inputId="auction-template-create-tab--fields--opening-time"
                    invalidBefore={date()}
                    timeFormat={HOUR_AND_MINUTE}
                    validator={validators.openingTime}
                />
                <TimeField
                    label={t('auction-templates.closing-time')}
                    state={closingTimeState}
                    className="auction-template-create-tab--fields--closing-time wide"
                    inputId="auction-template-create-tab--fields--closing-time"
                    invalidBefore={openingTimeState.value}
                    timeFormat={HOUR_AND_MINUTE}
                    validator={validators.closingTime}
                />
                {productType.is(ENERGY) && (
                    <>
                        <NumberField
                            label={t('auction-templates.delivery-year')}
                            className="auction-template-create-tab--fields--delivery-year"
                            inputId="auction-template-create-tab--fields--delivery-year"
                            state={deliveryYearState}
                            min={getCurrentYear()}
                            validator={validators.deliveryYear}
                            noThousandSeparator
                        />
                        <NumberField
                            label={t('auction-templates.number-of-lots')}
                            className="auction-template-create-tab--fields--number-of-lots"
                            inputId="auction-template-create-tab--fields--number-of-lots"
                            state={quantityValueState}
                            validator={validators.quantityValue}
                            step={Constants.DEFAULT_QUANTITY_STEP}
                        />
                        <NumberField
                            label={t('auction-templates.power-per-lot')}
                            className="auction-template-create-tab--fields--power-per-lot"
                            inputId="auction-template-create-tab--fields--power-per-lot"
                            state={powerState}
                            validator={validators.power}
                            postfix={<Unit unit={productState.value.quantityUnit.substring(0, productState.value.quantityUnit.length - 1)} />}
                        />
                    </>
                )}
                {productType.is(SECURITY) && (
                    <>
                        <NumberField
                            label={t('auction-templates.quantity')}
                            className="auction-template-create-tab--fields--quantity"
                            inputId="auction-template-create-tab--fields--quantity"
                            state={quantityValueState}
                            validator={validators.quantityValue}
                            step={Constants.DEFAULT_QUANTITY_STEP}
                        />
                        <SelectField
                            label={t('auction-templates.settlement-start')}
                            state={settlementStartTypeState}
                            className="auction-template-create-tab--fields--settlement-start"
                            inputId="auction-template-create-tab--fields--settlement-start"
                            emptyOption={<option value="">&nbsp;</option>}
                            options={availableSettlStartTypes.map((availableSettlementStartType) => (
                                <option value={availableSettlementStartType} key={availableSettlementStartType}>
                                    {getSettlementStartType(availableSettlementStartType).translate(t)}
                                </option>
                            ))}
                        />
                    </>
                )}
                {availableAuctionTypes.length > 1 && (
                    <SelectField
                        label={t('auction-templates.auction-type')}
                        state={auctionTypeState}
                        className="auction-template-create-tab--fields--auction-type"
                        inputId="auction-template-create-tab--fields--auction-type"
                        emptyOption={<option value="">&nbsp;</option>}
                        options={availableAuctionTypes.map((availableAuctionType) => (
                            <option value={availableAuctionType} key={availableAuctionType}>
                                {getAuctionType(availableAuctionType).translate(t)}
                            </option>
                        ))}
                        isRequired
                    />
                )}
                {availableBiddingTypes.length > 1 && (
                    <SelectField
                        label={t('auction-templates.bidding-type')}
                        onChange={onBiddingTypeSelect}
                        className="auction-template-create-tab--fields--bidding-type"
                        inputId="auction-template-create-tab--fields--bidding-type"
                        state={biddingTypeState}
                        value={biddingTypeState.value.serverType}
                        valueTransformer={keyToBiddingType}
                        emptyOption={<option value="">&nbsp;</option>}
                        options={availableBiddingTypes.map((availableBiddingType) => (
                            <option value={availableBiddingType} key={availableBiddingType}>
                                {getBiddingType(availableBiddingType).translate(t)}
                            </option>
                        ))}
                        isRequired
                    />
                )}
                {availableTradePriceCalculationTypes.length > 1 && (
                    <SelectField
                        label={t('auction-templates.trade-price-calculation-type')}
                        onChange={onTradePriceCalculationTypeSelect}
                        className="auction-template-create-tab--fields--trade-price-calculation-type"
                        inputId="auction-template-create-tab--fields--trade-price-calculation-type"
                        state={tradePriceCalculationTypeState}
                        valueTransformer={keyToTradePriceCalculationTypeSelect}
                        value={tradePriceCalculationTypeState.value.serverType}
                        emptyOption={<option value="">&nbsp;</option>}
                        options={availableTradePriceCalculationTypes.map((availableTradePriceCalculationType) => (
                            <option value={availableTradePriceCalculationType} key={availableTradePriceCalculationType}>
                                {getTradePriceCalculationType(availableTradePriceCalculationType).translate(t)}
                            </option>
                        ))}
                        isRequired
                    />
                )}
                {availableAuctionAlgorithms.length > 1 && (
                    <SelectField
                        label={t('auction-templates.algorithm')}
                        state={algorithmState}
                        className="auction-template-create-tab--fields--algorithm"
                        inputId="auction-template-create-tab--fields--algorithm"
                        emptyOption={<option value="">&nbsp;</option>}
                        options={availableAuctionAlgorithms.map((availableAuctionAlgorithm) => (
                            <option value={availableAuctionAlgorithm} key={availableAuctionAlgorithm}>
                                {getAuctionAlgorithm(availableAuctionAlgorithm).translate(t)}
                            </option>
                        ))}
                        isRequired
                    />
                )}
                {availableAuctionAcceptanceTypes.length > 1 && (
                    <SelectField
                        label={t('auction-templates.auction-acceptance-type')}
                        state={acceptanceTypeState}
                        className="auction-template-create-tab--fields--auction-acceptance-type"
                        inputId="auction-template-create-tab--fields--auction-acceptance-type"
                        emptyOption={<option value="">&nbsp;</option>}
                        options={availableAuctionAcceptanceTypes.map((availableAcceptanceType) => (
                            <option value={availableAcceptanceType} key={availableAcceptanceType}>
                                {getAuctionAcceptanceType(availableAcceptanceType).translate(t)}
                            </option>
                        ))}
                        isRequired
                    />
                )}
                {tradePriceCalculationTypeState.value.is(BID_PRICE) && (
                    <CheckboxField
                        label={t('auction-templates.show-last-price')}
                        state={showLastPriceState}
                        className="auction-template-create-tab--fields--show-last-price"
                        inputId="auction-template-create-tab--fields--show-last-price"
                    />
                )}
                {biddingTypeState.value.is(SLOTTED) && (
                    <>
                        <CheckboxField
                            label={t('auction-templates.show-ranking')}
                            state={showRankingState}
                            className="auction-template-create-tab--fields--show-ranking"
                            inputId="auction-template-create-tab--fields--show-ranking"
                        />
                        <CheckboxField
                            label={t('auction-templates.limit-bid-modification')}
                            state={limitBidModificationState}
                            className="auction-template-create-tab--fields--limit-bid-modification"
                            inputId="auction-template-create-tab--fields--limit-bid-modification"
                        />
                    </>
                )}
                {biddingTypeState.value.is(OPEN) && (
                    <>
                        <NumberField
                            label={t('auction-templates.smallest-quantity-unit')}
                            className="auction-template-create-tab--fields--smallest-quantity-unit"
                            inputId="auction-template-create-tab--fields--smallest-quantity-unit"
                            state={smallestQuantityUnitState}
                            min={Constants.MIN_SMALLEST_UNIT}
                            max={quantityValueState.value}
                            validator={smallestQuantityUnitValidator}
                            step={Constants.DEFAULT_QUANTITY_STEP}
                            validateOnValidatorChange
                        />
                        <NumberField
                            label={t('auction-templates.max-bid-size')}
                            className="auction-template-create-tab--fields--max-bid-size"
                            inputId="auction-template-create-tab--fields--max-bid-size"
                            state={maxBidSizeState}
                            min={Constants.MIN_MAXIMUM_BID_SIZE}
                            max={quantityValueState.value}
                            validator={maxBidSizeValidator}
                            step={smallestQuantityUnitState.value || Constants.DEFAULT_QUANTITY_STEP}
                            validateOnValidatorChange
                        />
                    </>
                )}
                <NumberField
                    label={t('auction-templates.min-price')}
                    className="auction-template-create-tab--fields--min-price"
                    inputId="auction-template-create-tab--fields--min-price"
                    state={minPriceState}
                    max={maxPriceState.value}
                    fractionDigits={2}
                    validator={minPriceValidator}
                    postfix={currencyPostfix}
                    step={Constants.DEFAULT_PRICE_STEP}
                    validateOnValidatorChange
                />
                <NumberField
                    label={t('auction-templates.max-price')}
                    className="auction-template-create-tab--fields--max-price"
                    inputId="auction-template-create-tab--fields--max-price"
                    state={maxPriceState}
                    min={minPriceState.value}
                    fractionDigits={2}
                    validator={maxPriceValidator}
                    postfix={currencyPostfix}
                    step={Constants.DEFAULT_PRICE_STEP}
                    validateOnValidatorChange
                />
                <NumberField
                    label={t('auction-templates.reference-price')}
                    className="auction-template-create-tab--fields--reference-price"
                    inputId="auction-template-create-tab--fields--reference-price"
                    state={spotReferencePriceState}
                    min={Constants.MIN_SPOT_REFERENCE_PRICE}
                    max={Constants.MAX_SPOT_REFERENCE_PRICE}
                    fractionDigits={2}
                    validator={validators.spotReferencePrice}
                    postfix={currencyPostfix}
                    step={Constants.DEFAULT_PRICE_STEP}
                />
                <NumberField
                    label={t('auction-templates.absolute-deviation')}
                    className="auction-template-create-tab--fields--absolute-deviation"
                    inputId="auction-template-create-tab--fields--absolute-deviation"
                    state={absoluteDeviationState}
                    min={Constants.MIN_ABSOLUTE_DEVIATION}
                    max={Constants.MAX_ABSOLUTE_DEVIATION}
                    fractionDigits={2}
                    validator={validators.absoluteDeviation}
                    postfix={currencyPostfix}
                    step={Constants.DEFAULT_PRICE_STEP}
                />
                <NumberField
                    label={t('auction-templates.relative-deviation')}
                    className="auction-template-create-tab--fields--relative-deviation"
                    inputId="auction-template-create-tab--fields--relative-deviation"
                    state={relativeDeviationState}
                    min={Constants.MIN_RELATIVE_DEVIATION}
                    max={Constants.MAX_RELATIVE_DEVIATION}
                    fractionDigits={2}
                    validator={validators.relativeDeviation}
                    postfix={<>&nbsp;%</>}
                    step={Constants.DEFAULT_PRICE_STEP}
                />
                <TextField
                    label={t('auction-templates.description')}
                    state={descriptionState}
                    className="auction-template-create-tab--fields--description x-wide"
                    inputId="auction-template-create-tab--fields--description"
                    validator={validators.description}
                    maxLength={AUCTION_TEMPLATE_DESCRIPTION_MAX_LENGTH}
                    placeholder={t('auctions.placeholder.description')}
                />
            </div>
            <div className="rich-list-row-expanded--buttons">
                <SaveButton
                    extendedMutation={extendedMutation}
                    disabled={isDisabled}
                    confirmationMessage={t('auction-templates.confirm-create-auction-template', { name: nameState.value })}
                />
            </div>
        </ExpandedListTab>
    );
}

export default AuctionTemplateCreateTab;
